import { useLastAppStateStore } from '@app/providers/router/model/store/last-app-state-store'
import { useChannels } from '@entities/channel'
import type { Post } from '@entities/post'
import { AddPostMobileModal, EditPostMobileModal, EmptyPost, MobilePost, usePosts } from '@entities/post'
import { savePayload } from '@entities/post/api'
import { EDisplayPosts } from '@pages/scheduled-posts-page/ui/scheduled-posts-page'
import { randomId } from '@shared/lib/randomId/randomId'
import { BottomNavMobile } from '@widgets/page-template/ui/bottom-nav-mobile'
import { PostingMobileHeader } from '@widgets/posting-mobile-header'
import { Button, Col, Row, theme } from 'antd'
import { InfiniteScroll } from 'antd-mobile'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router'
import { useShallow } from 'zustand/react/shallow'
import * as styles from './draft-posts-page.mobile.module.scss'

const BOT_USERNAME = process.env.BOT_USERNAME || ''

export const DraftPostsPage = () => {
    const {
        token: { colorBgContainer }
    } = theme.useToken()

    const { currentChannel } = useChannels()

    const { sessionId, action, postId, setState, reset } = useLastAppStateStore(
        useShallow(({ sessionId, action, postId, setState, reset }) => ({ sessionId, action, postId, setState, reset }))
    )

    const {
        currentPost,
        posts,
        postTypes,
        selectedPostTypes,
        isPostsLoading,
        actions: { setPagination, setSelectedPostTypes }
    } = usePosts({
        state: 'draft',
        display: EDisplayPosts.all,
        channelId: currentChannel?.id,
        postId: postId ? postId : undefined
    })

    const hasNextPage = useMemo(() => !!posts?.next, [posts?.next])

    const fetchNextPage = useCallback(async () => {
        if (isPostsLoading) return
        setPagination((prev) => ({ page: prev.page + 1, pageSize: prev.pageSize }))
    }, [isPostsLoading, setPagination])

    const [editPostData, setEditPost] = useState<Post | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

    const onEditModalOpen = (post: Post) => {
        setEditPost(post)
        setTimeout(() => {
            setIsEditModalOpen(true)
        }, 0)
    }

    const onEditModalClose = () => {
        setEditPost(null)
        setIsEditModalOpen(false)
        reset()
    }

    const onCloseAddPostModal = () => {
        setIsCreateModalOpen(false)
        reset()
    }

    const location = useLocation()

    const onCreatePost = () => {
        const tg = (window as any).Telegram?.WebApp

        const sessionId = randomId()

        tg.showConfirm('Для создания поста, необходимо перейти в бот', async (ok?: boolean) => {
            if (!ok) return

            try {
                setState({ page: location.pathname, action: 'create-post', sessionId: sessionId })

                const payloadId = await savePayload({
                    action: 'create_post',
                    session_id: sessionId,
                    platform: 'mobile'
                })

                const url = `https://t.me/${BOT_USERNAME}?start=payload_${payloadId}`
                tg.openTelegramLink(url)
                tg.close()
            } catch (error) {
                alert(error)
            }
        })
    }

    useEffect(() => {
        if (action === 'create-post') {
            setIsCreateModalOpen(true)
        } else if (action === 'edit-post' && currentPost) {
            onEditModalOpen(currentPost)
        }
    }, [currentPost, action])

    return (
        <>
            <PostingMobileHeader
                title="Черновики"
                current="draft"
                postTypes={postTypes}
                selectedPostTypes={selectedPostTypes}
                setSelectedPostTypes={setSelectedPostTypes}
            />
            <div
                style={{
                    position: 'relative',
                    background: colorBgContainer,
                    padding: '20px 16px',
                    overflow: 'auto',
                    flexGrow: 1
                }}
            >
                {posts?.results.length ? (
                    <Row justify={'end'} gutter={[0, 25]}>
                        {posts?.results.map((p) => (
                            <Col span={20} key={p.id}>
                                <MobilePost post={p} onEdit={() => onEditModalOpen(p)} />
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Row justify={'end'}>
                        <Col span={20}>
                            <EmptyPost title="Пока у вас нет черновиков. Создайте новый пост, и сохраните его в черновик" />
                        </Col>
                    </Row>
                )}

                <InfiniteScroll loadMore={fetchNextPage} hasMore={!!hasNextPage}>
                    {null}
                </InfiniteScroll>
                <Button size="large" type="primary" className={styles.newPostBtn} onClick={onCreatePost}>
                    Новый пост
                </Button>

                {isCreateModalOpen && sessionId && (
                    <AddPostMobileModal open={isCreateModalOpen} onClose={onCloseAddPostModal} sessionId={sessionId} />
                )}

                {editPostData && (
                    <EditPostMobileModal
                        editPostData={editPostData}
                        open={isEditModalOpen}
                        sessionId={sessionId}
                        onClose={onEditModalClose}
                    />
                )}
            </div>
            <BottomNavMobile />
        </>
    )
}
